import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, FloppyDisk, ArrowCounterClockwise, PaperPlaneTilt, Code, TextAa, CaretDown, CaretRight,
} from '@phosphor-icons/react';
import Editor from '@monaco-editor/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailTemplateEditor({ templateKey, onBack }) {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [mode, setMode] = useState('simple'); // 'simple' | 'expert'

  // Editor state
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [showVersions, setShowVersions] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const textareaRef = useRef(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [templateKey]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/email-templates/${templateKey}`,
        { withCredentials: true },
      );
      setTemplate(data);
      setSubject(data.subject || '');
      setHtmlContent(data.html_content || '');
      setIsDirty(false);
    } catch (e) {
      toast.error("Erreur de chargement du modèle d'e-mail");
    } finally {
      setLoading(false);
    }
  };

  // Preview live (debounce 500ms)
  useEffect(() => {
    if (!template) return;
    const timer = setTimeout(() => { updatePreview(); }, 500);
    return () => clearTimeout(timer);
    /* eslint-disable-next-line */
  }, [subject, htmlContent]);

  const updatePreview = async () => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/email-templates/${templateKey}/preview`,
        { subject, html_content: htmlContent },
        { withCredentials: true },
      );
      setPreviewHtml(data.html_full);
    } catch (e) {
      console.error('Preview error:', e);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API_URL}/api/admin/email-templates/${templateKey}`,
        { subject, html_content: htmlContent },
        { withCredentials: true },
      );
      toast.success(`Modèle enregistré (v${data.version})`);
      setIsDirty(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm(
      'Revenir au modèle par défaut ?\n\n'
      + 'Toutes vos modifications (et l\'historique des versions) seront perdues.',
    )) return;
    try {
      await axios.delete(
        `${API_URL}/api/admin/email-templates/${templateKey}`,
        { withCredentials: true },
      );
      toast.success('Modèle par défaut rétabli');
      await load();
    } catch (e) {
      toast.error('Erreur réinitialisation');
    }
  };

  const testSend = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/email-templates/${templateKey}/test-send`,
        { subject, html_content: htmlContent },
        { withCredentials: true },
      );
      if (data.success) {
        toast.success(`Email test envoyé à votre adresse`);
      } else {
        toast.error(`Erreur envoi : ${data.error || 'inconnue'}`);
      }
    } catch (e) {
      toast.error('Erreur envoi test');
    } finally {
      setTesting(false);
    }
  };

  const restoreVersion = async (version) => {
    if (!window.confirm(`Restaurer la version ${version} ? La version actuelle sera ajoutée à l'historique.`)) return;
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/email-templates/${templateKey}/restore/${version}`,
        {},
        { withCredentials: true },
      );
      toast.success(`Version ${version} restaurée (v${data.new_version})`);
      await load();
    } catch (e) {
      toast.error('Erreur restauration');
    }
  };

  const insertVariable = (varName) => {
    const placeholder = '{' + varName + '}';
    if (mode === 'simple' && textareaRef.current) {
      const ta = textareaRef.current;
      const start = ta.selectionStart || 0;
      const end = ta.selectionEnd || 0;
      const next = htmlContent.slice(0, start) + placeholder + htmlContent.slice(end);
      setHtmlContent(next);
      setIsDirty(true);
      setTimeout(() => {
        ta.focus();
        ta.selectionStart = ta.selectionEnd = start + placeholder.length;
      }, 0);
    } else {
      navigator.clipboard?.writeText(placeholder);
      toast.success(`${placeholder} copié dans le presse-papier`);
    }
  };

  if (loading) return <div className="p-8 text-center text-sm text-[#6B6B6B]">Chargement…</div>;
  if (!template) return null;

  return (
    <div className="flex flex-col" data-testid="email-template-editor" style={{ height: 'calc(100vh - 200px)', minHeight: '600px' }}>
      {/* Toolbar */}
      <div className="border-b border-[#E0E0DB] p-3 flex items-center gap-3 flex-wrap bg-white">
        <button
          onClick={onBack}
          className="text-sm flex items-center gap-1 px-3 py-1.5 hover:bg-[#F5F5F3] transition-colors"
          data-testid="btn-editor-back"
        >
          <ArrowLeft size={16} /> Retour
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="font-bold text-sm text-[#2C2C2C] truncate" data-testid="editor-template-key">{templateKey}</h2>
          <p className="text-xs text-[#9A9A9A]">
            {template.is_custom ? `Personnalisé v${template.version}` : 'Par défaut Kroquis'}
            {isDirty && <span className="ml-2 text-[#C75B39] font-bold">• Modifications non sauvegardées</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setMode(mode === 'simple' ? 'expert' : 'simple')}
            className="text-xs flex items-center gap-1 px-3 py-1.5 border border-[#E0E0DB] hover:bg-[#F5F5F3] transition-colors"
            data-testid="btn-toggle-mode"
          >
            {mode === 'simple' ? <><Code size={14} /> Mode Expert</> : <><TextAa size={14} /> Mode Simple</>}
          </button>
          <button
            onClick={testSend}
            disabled={testing}
            className="text-xs flex items-center gap-1 px-3 py-1.5 border border-[#E0E0DB] hover:bg-[#F5F5F3] transition-colors disabled:opacity-50"
            data-testid="btn-test-send"
          >
            <PaperPlaneTilt size={14} /> {testing ? 'Envoi…' : 'Test envoi'}
          </button>
          {template.is_custom && (
            <button
              onClick={reset}
              className="text-xs flex items-center gap-1 px-3 py-1.5 border border-[#FFEBEE] text-[#8B3A3A] hover:bg-[#FFEBEE] transition-colors"
              data-testid="btn-reset"
            >
              <ArrowCounterClockwise size={14} /> Réinitialiser
            </button>
          )}
          <button
            onClick={save}
            disabled={saving}
            className="text-xs flex items-center gap-1 px-4 py-1.5 bg-[#C75B39] text-white hover:bg-[#A84A2D] transition-colors disabled:opacity-50 font-bold"
            data-testid="btn-save"
          >
            <FloppyDisk size={14} /> {saving ? 'Sauvegarde…' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Variables disponibles */}
      {template.variables && template.variables.length > 0 && (
        <div className="border-b border-[#E0E0DB] p-3 bg-[#FAFAF8]">
          <p className="text-xs uppercase tracking-wider font-bold mb-1.5 text-[#6B6B6B]">
            Variables disponibles {mode === 'expert' && '(clic = copier)'}
          </p>
          <div className="flex flex-wrap gap-1">
            {template.variables.map(v => (
              <button
                key={v.name}
                onClick={() => insertVariable(v.name)}
                className="text-xs px-2 py-1 bg-white border border-[#E0E0DB] hover:bg-[#FFF3E0] hover:border-[#C75B39] transition-colors flex items-center gap-1"
                title={`Exemple : ${v.example}`}
                data-testid={`btn-var-${v.name}`}
              >
                <span className="font-mono text-[#C75B39]">{`{${v.name}}`}</span>
                <span className="text-[#9A9A9A]">{v.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Subject */}
      <div className="border-b border-[#E0E0DB] p-3 bg-white">
        <label className="text-xs uppercase tracking-wider font-bold mb-1 block text-[#6B6B6B]">
          Sujet de l'email
        </label>
        <input
          type="text"
          value={subject}
          onChange={e => { setSubject(e.target.value); setIsDirty(true); }}
          className="w-full border border-[#E0E0DB] px-3 py-2 text-sm focus:outline-none focus:border-[#C75B39]"
          placeholder="Ex: Appel d'offres — {project_name}"
          data-testid="input-subject"
        />
      </div>

      {/* Editor + Preview split */}
      <div className="flex-1 flex min-h-0">
        <div className="flex-1 border-r border-[#E0E0DB] min-w-0 flex flex-col">
          <div className="p-2 border-b border-[#E0E0DB] bg-[#FAFAF8] text-xs text-[#6B6B6B] font-bold uppercase tracking-wider">
            Éditeur — {mode === 'expert' ? 'HTML (Monaco)' : 'HTML simple'}
          </div>
          <div className="flex-1 min-h-0">
            {mode === 'expert' ? (
              <Editor
                height="100%"
                defaultLanguage="html"
                value={htmlContent}
                onChange={(v) => { setHtmlContent(v || ''); setIsDirty(true); }}
                theme="vs-dark"
                options={{
                  minimap: { enabled: false },
                  fontSize: 12,
                  wordWrap: 'on',
                  scrollBeyondLastLine: false,
                  formatOnPaste: true,
                }}
              />
            ) : (
              <textarea
                ref={textareaRef}
                value={htmlContent}
                onChange={e => { setHtmlContent(e.target.value); setIsDirty(true); }}
                className="w-full h-full p-3 border-0 font-mono text-xs leading-relaxed resize-none focus:outline-none"
                placeholder="Tapez votre contenu HTML ici. Les balises HTML sont supportées : <h1>, <p>, <strong>, etc."
                data-testid="textarea-simple"
              />
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0 flex flex-col bg-[#F5F5F3]">
          <div className="p-2 border-b border-[#E0E0DB] bg-[#FAFAF8] text-xs text-[#6B6B6B] font-bold uppercase tracking-wider flex items-center justify-between">
            <span>Aperçu live (données factices)</span>
            <span className="text-[#9A9A9A] font-normal lowercase">debounce 500ms</span>
          </div>
          <iframe
            srcDoc={previewHtml}
            className="flex-1 bg-white border-0"
            title="Preview"
            sandbox="allow-same-origin"
            data-testid="preview-iframe"
          />
        </div>
      </div>

      {/* History */}
      {template.history && template.history.length > 0 && (
        <div className="border-t border-[#E0E0DB] bg-white">
          <button
            onClick={() => setShowVersions(!showVersions)}
            className="w-full text-xs font-bold uppercase tracking-wider p-3 text-left text-[#2C2C2C] hover:bg-[#FAFAF8] flex items-center gap-2 transition-colors"
            data-testid="btn-toggle-history"
          >
            {showVersions ? <CaretDown size={14} /> : <CaretRight size={14} />}
            Historique ({template.history.length} version{template.history.length > 1 ? 's' : ''})
          </button>
          {showVersions && (
            <div className="px-3 pb-3 space-y-1">
              {template.history.slice().reverse().map(h => (
                <div
                  key={h.version}
                  className="flex items-center gap-3 text-xs py-1.5 px-2 hover:bg-[#FAFAF8] transition-colors"
                  data-testid={`history-row-v${h.version}`}
                >
                  <span className="font-mono font-bold text-[#C75B39]">v{h.version}</span>
                  <span className="text-[#9A9A9A] flex-1">
                    {new Date(h.saved_at).toLocaleString('fr-FR')}
                    {h.saved_by_user_name ? ` • ${h.saved_by_user_name}` : ''}
                  </span>
                  <button
                    onClick={() => restoreVersion(h.version)}
                    className="text-[#1E3A5F] hover:underline font-medium"
                    data-testid={`btn-restore-v${h.version}`}
                  >
                    Restaurer
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
